import React, { useState, useEffect, Fragment } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import Layout from "../../components/global/layout";

// ── Input Field Component (moved OUTSIDE component to prevent remount on re-render) ──
const FormField = ({
  label,
  name,
  value,
  onChange,
  placeholder,
  error,
  required = false,
  type = "text",
}) => (
  <div className="flex flex-col gap-1">
    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`border rounded-xl px-3.5 py-2.5 text-sm text-gray-800 outline-none transition-all focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] ${
        error ? "border-red-400 bg-red-50/30" : "border-gray-200 bg-white"
      }`}
    />
    {error && <p className="text-xs text-red-500 mt-0.5">{error}</p>}
  </div>
);

// ── Status Badge (moved OUTSIDE component for same reason) ──
const StatusBadge = ({ active }) => (
  <span
    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${
      active
        ? "bg-[#F0FEF4] text-[#008236] border-[#0a7822a6] border"
        : "bg-gray-100 text-gray-500 border border-gray-200"
    }`}
  >
    <span
      className={`size-1.5 rounded-full ${active ? "bg-[#0a7822a6]" : "bg-gray-400"}`}
    />
    {active ? "Active" : "Inactive"}
  </span>
);

export default function QuickBookCustomerList() {
  const user = useSelector((state) => state.user);
  const history = useNavigate();

  const isConnected = !!user?.quick_book_accesToken;

  const [customers, setCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // "" | "active" | "inactive"
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  // ── Add Customer Modal State ──
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [addForm, setAddForm] = useState({
    name: "",
    email: "",
    bill_line1: "",
    bill_city: "",
    bill_country: "",
    bill_postal_code: "",
    ship_line1: "",
    ship_city: "",
    ship_country: "",
    ship_postal_code: "",
  });
  const [addErrors, setAddErrors] = useState({});

  // ── Fetch customers ──
  const fetchCustomers = async () => {
    loader(true);
    await ApiClient.post("quickbook/customers", {
      quickbook_token: user?.quick_book_accesToken || "",
      company: user?.quick_book_companyId,
    })
      .then((res) => {
        if (res?.success) setCustomers(res?.data || []);
      })
      .catch(console.error);
    loader(false);
  };

  useEffect(() => {
    if (isConnected) fetchCustomers();
  }, []);

  // ── Filtered list ──
  const filteredCustomers = customers.filter((c) => {
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      !q ||
      c.DisplayName?.toLowerCase().includes(q) ||
      c.CompanyName?.toLowerCase().includes(q) ||
      c.PrimaryEmailAddr?.Address?.toLowerCase().includes(q) ||
      c.PrimaryPhone?.FreeFormNumber?.toLowerCase().includes(q);

    const matchesStatus =
      statusFilter === ""
        ? true
        : statusFilter === "active"
          ? c.Active === true
          : c.Active === false;

    return matchesSearch && matchesStatus;
  });

  // ── Derived stats ──
  const activeCount = customers.filter((c) => c.Active).length;
  const totalBalance = customers.reduce((s, c) => s + (c.Balance || 0), 0);
  const withBalanceCount = customers.filter((c) => c.Balance > 0).length;

  // ── Helpers ──
  const formatCurrency = (val, code = "USD") =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: code,
    }).format(val ?? 0);

  const formatDate = (str) => {
    if (!str) return "—";
    const d = new Date(str);
    if (isNaN(d)) return "—";
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getInitials = (name = "") => {
    const parts = name.trim().split(" ").filter(Boolean);
    if (!parts.length) return "?";
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const formatAddress = (addr) => {
    if (!addr) return "—";
    const parts = [
      addr.Line1,
      addr.City,
      addr.CountrySubDivisionCode,
      addr.PostalCode,
    ].filter(Boolean);
    return parts.join(", ") || "—";
  };

  // ── Add Customer Handlers ──
  const handleAddFormChange = (e) => {
    const { name, value } = e.target;
    setAddForm((prev) => ({ ...prev, [name]: value }));
    if (addErrors[name]) setAddErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateAddForm = () => {
    const errs = {};
    if (!addForm.name.trim()) errs.name = "Name is required";
    if (!addForm.email.trim()) {
      errs.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addForm.email)) {
      errs.email = "Enter a valid email";
    }
    return errs;
  };

  const handleAddCustomer = async () => {
    const errs = validateAddForm();
    if (Object.keys(errs).length) {
      setAddErrors(errs);
      return;
    }

    setAddLoading(true);
    const payload = {
      quickbook_token: user?.quick_book_accesToken || "",
      company: user?.quick_book_companyId,
      name: addForm.name,
      email: addForm.email,
      BillAddr: {
        bill_line1: addForm.bill_line1,
        bill_city: addForm.bill_city,
        bill_country: addForm.bill_country,
        bill_postal_code: addForm.bill_postal_code,
      },
      ShipAddr: {
        ship_line1: addForm.ship_line1,
        ship_city: addForm.ship_city,
        ship_country: addForm.ship_country,
        ship_postal_code: addForm.ship_postal_code,
      },
    };

    await ApiClient.post("quickbook/add/customer", payload)
      .then((res) => {
        if (res?.success) {
          closeAddModal();
          fetchCustomers();
        }
      })
      .catch(console.error);

    setAddLoading(false);
  };

  const closeAddModal = () => {
    setIsAddModalOpen(false);
    setAddForm({
      name: "",
      email: "",
      bill_line1: "",
      bill_city: "",
      bill_country: "",
      bill_postal_code: "",
      ship_line1: "",
      ship_city: "",
      ship_country: "",
      ship_postal_code: "",
    });
    setAddErrors({});
  };

  const STATUS_TABS = [
    { label: "All", value: "" },
    { label: "Active", value: "active" },
    { label: "Inactive", value: "inactive" },
  ];

  const closeModal = () => {
    setIsViewModalOpen(false);
    setSelectedCustomer(null);
  };

  // ── Not Connected State ──
  if (!isConnected) {
    return (
      <Layout>
        <div className="max-xl:container xl:max-w-[1280px]  mx-auto">
          <div className="flex flex-col gap-1 mb-6">
            <h2 className="text-gray-900 text-xl sm:text-2xl font-semibold">
              Customers
            </h2>
            <p className="text-sm sm:text-md text-gray-600">
              Manage your QuickBooks customers
            </p>
          </div>

          <div className="w-full border rounded-2xl bg-white p-5 lg:p-8">
            <div className="flex flex-col items-center justify-center py-20 gap-6 text-center">
              <div className="size-20 rounded-2xl bg-[#2CA01C]/10 border border-[#2CA01C]/20 flex items-center justify-center">
                <svg
                  viewBox="0 0 40 40"
                  width="40"
                  height="40"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="18"
                    cy="18"
                    r="10"
                    fill="none"
                    stroke="#2CA01C"
                    strokeWidth="3"
                  />
                  <line
                    x1="25"
                    y1="25"
                    x2="31"
                    y2="31"
                    stroke="#2CA01C"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <div className="space-y-1.5">
                <h3 className="text-lg font-semibold text-gray-900">
                  QuickBooks Not Connected
                </h3>
                <p className="text-sm text-gray-500 max-w-sm">
                  Connect your QuickBooks account to view and manage your
                  customers seamlessly.
                </p>
              </div>
              <button
                type="button"
                onClick={() => history("/quickbook/integration")}
                className="w-fit inline-flex items-center gap-2 bg-[#115B9D] hover:opacity-80 text-white text-sm rounded-xl transition-colors font-medium px-5 py-2.5"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
                Connect QuickBooks
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // ── Main Listing ──
  return (
    <Layout>
      <div className="max-xl:container xl:max-w-[1280px]  mx-auto">
        {/* ─── Page Header ─── */}
        <div className="flex flex-wrap items-center justify-between gap-2 mb-6">
          <div className="flex flex-col gap-1">
            <h2 className="text-gray-900 text-xl sm:text-2xl font-semibold">
              Customers
            </h2>
            <p className="text-sm sm:text-md text-gray-600">
              {customers.length} customer{customers.length !== 1 ? "s" : ""}{" "}
              synced
              {withBalanceCount > 0 && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-md bg-amber-100 text-amber-700 text-xs font-semibold">
                  {withBalanceCount} with balance
                </span>
              )}
            </p>
          </div>

          {/* ─── Add Customer Button ─── */}
          <button
            type="button"
            onClick={() => setIsAddModalOpen(true)}
            className="w-fit inline-flex items-center gap-2 bg-[#115B9D] hover:opacity-80 text-white text-sm rounded-xl transition-colors font-medium px-5 py-2.5"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <line x1="19" y1="8" x2="19" y2="14" />
              <line x1="22" y1="11" x2="16" y2="11" />
            </svg>
            Add Customer
          </button>
        </div>

        {/* ─── Summary Cards ─── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {[
            {
              label: "Total Customers",
              value: customers.length,
              icon: (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-[#115B9D]"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              ),
              bg: "bg-[#115B9D17]",
            },
            {
              label: "Active Customers",
              value: activeCount,
              icon: (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-[#008236]"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              ),
              bg: "bg-[#f0fdf4]",
            },
            {
              label: "Total Outstanding",
              value: formatCurrency(totalBalance),
              icon: (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-amber-600"
                >
                  <line x1="12" y1="1" x2="12" y2="23" />
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                </svg>
              ),
              bg: "bg-amber-50",
            },
          ].map((card) => (
            <div
              key={card.label}
              className="border border-gray-200 rounded-2xl bg-white px-5 py-4 flex items-center gap-4"
            >
              <div
                className={`size-10 rounded-xl ${card.bg} flex items-center justify-center shrink-0`}
              >
                {card.icon}
              </div>
              <div>
                <p className="text-xs text-gray-600 font-medium">
                  {card.label}
                </p>
                <p className="text-xl font-semibold text-gray-900 mt-0.5">
                  {card.value}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="w-full border rounded-2xl bg-white p-5 lg:p-8">
          {/* ─── Filters Row ─── */}
          <div className="flex justify-between flex-wrap gap-3 mb-4">
            {/* Status Tabs */}
            <div className="max-sm:w-full overflow-auto max-sm:w-full overflow-auto w-full sm:w-fit overflow-auto flex items-center gap-1 bg-gray-100 rounded-xl p-1.5">
              {STATUS_TABS.map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => setStatusFilter(tab.value)}
                  className={`whitespace-nowrap px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    statusFilter === tab.value
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative w-full sm:w-[350px]">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600"
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
              <input
                type="text"
                placeholder="Search by name, email, phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full border border-gray-200 rounded-xl pl-9 pr-4 py-2.5 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all"
              />
            </div>
          </div>

          {/* ─── Table ─── */}

          {/* Desktop Table */}
          <div className="hidden md:block overflow-hidden border border-gray-200 rounded-xl">
            <table className="w-full table-fixed">
              <colgroup>
                <col style={{ width: "8%" }} /> {/* Actions */}
                <col style={{ width: "17%" }} /> {/* Customer */}
                <col style={{ width: "18%" }} /> {/* Contact */}
                <col style={{ width: "17%" }} /> {/* Address */}
                <col style={{ width: "12%" }} /> {/* Balance */}
                <col style={{ width: "12%" }} /> {/* Status */}
                <col style={{ width: "16%" }} /> {/* Last Updated */}
              </colgroup>
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {[
                    "Actions",
                    "Customer",
                    "Contact",
                    "Address",
                    "Balance",
                    "Status",
                    "Last Updated",
                  ].map((h) => (
                    <th key={h} className="text-left py-3 px-3">
                      <span className="text-xs text-gray-700 font-medium whitespace-normal leading-tight block">
                        {h}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredCustomers.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-16">
                      <div className="flex flex-col items-center gap-3">
                        <div className="size-14 rounded-full bg-gray-100 flex items-center justify-center">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="text-gray-600"
                          >
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                            <circle cx="9" cy="7" r="4" />
                          </svg>
                        </div>
                        <p className="text-sm text-gray-500">
                          No customers found
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredCustomers.map((item, index) => (
                    <tr
                      key={item.Id || index}
                      className="hover:bg-gray-50/60 transition-colors align-middle"
                    >
                      {/* Actions */}
                      <td className="py-2.5 px-3 align-middle">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedCustomer(item);
                            setIsViewModalOpen(true);
                          }}
                          title="View Details"
                          className="size-7 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-[#115B9D] transition-colors"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        </button>
                      </td>

                      {/* Customer */}
                      <td className="py-2.5 px-3 align-middle overflow-hidden">
                        <div className="flex items-start gap-2 min-w-0">
                          <div className="size-8 rounded-xl bg-[#115B9D17] border border-[#115B9D]/10 flex items-center justify-center shrink-0">
                            <span className="text-[10px] font-semibold text-[#115B9D]">
                              {getInitials(item.DisplayName)}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-gray-800 break-words leading-tight">
                              {item.DisplayName || "—"}
                            </p>
                            {item.CompanyName &&
                              item.CompanyName !== item.DisplayName && (
                                <p className="text-[11px] text-gray-600 break-words leading-tight">
                                  {item.CompanyName}
                                </p>
                              )}
                          </div>
                        </div>
                      </td>

                      {/* Contact */}
                      <td className="py-2.5 px-3 align-middle overflow-hidden">
                        <p className="text-xs text-gray-700 break-all leading-tight">
                          {item.PrimaryEmailAddr?.Address || "—"}
                        </p>
                        {item.PrimaryPhone?.FreeFormNumber && (
                          <p className="text-[11px] text-gray-600 mt-0.5 whitespace-nowrap">
                            {item.PrimaryPhone.FreeFormNumber}
                          </p>
                        )}
                      </td>

                      {/* Address — full show, no truncate */}
                      <td className="py-2.5 px-3 align-middle overflow-hidden">
                        <p className="text-xs text-gray-500 break-words leading-snug">
                          {formatAddress(item.BillAddr)}
                        </p>
                      </td>

                      {/* Balance */}
                      <td className="py-2.5 px-3 align-middle">
                        <p
                          className={`text-xs font-semibold whitespace-nowrap ${
                            item.Balance > 0
                              ? "text-amber-600"
                              : "text-[#008236]"
                          }`}
                        >
                          {formatCurrency(
                            item.Balance,
                            item.CurrencyRef?.value,
                          )}
                        </p>
                      </td>

                      {/* Status */}
                      <td className="py-2.5 px-3 align-middle">
                        <StatusBadge active={item.Active} />
                      </td>

                      {/* Last Updated */}
                      <td className="py-2.5 px-3 align-middle">
                        <p className="text-xs text-gray-700 break-words leading-tight">
                          {formatDate(item.MetaData?.LastUpdatedTime)}
                        </p>
                        <p className="text-[11px] text-gray-600 break-words leading-tight">
                          Created {formatDate(item.MetaData?.CreateTime)}
                        </p>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden">
            {filteredCustomers.length === 0 ? (
              <div className="border border-gray-200 rounded-xl py-16 flex flex-col items-center gap-3">
                <div className="size-14 rounded-full bg-gray-100 flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-gray-600"
                  >
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                  </svg>
                </div>
                <p className="text-sm text-gray-500">No customers found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredCustomers.map((item, index) => (
                  <div
                    key={item.Id || index}
                    className="border border-gray-200 rounded-xl p-4 bg-white"
                  >
                    {/* Top row: Customer + Actions */}
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="flex items-start gap-2.5 min-w-0">
                        <div className="size-9 rounded-xl bg-[#115B9D17] border border-[#115B9D]/10 flex items-center justify-center shrink-0">
                          <span className="text-xs font-semibold text-[#115B9D]">
                            {getInitials(item.DisplayName)}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-800 break-words">
                            {item.DisplayName || "—"}
                          </p>
                          {item.CompanyName &&
                            item.CompanyName !== item.DisplayName && (
                              <p className="text-xs text-gray-600 break-words">
                                {item.CompanyName}
                              </p>
                            )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedCustomer(item);
                          setIsViewModalOpen(true);
                        }}
                        title="View Details"
                        className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-[#115B9D] transition-colors shrink-0"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="15"
                          height="15"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      </button>
                    </div>

                    {/* Contact */}
                    <div className="py-1.5 border-t border-gray-100">
                      <span className="text-xs text-gray-500 block mb-0.5">
                        Contact
                      </span>
                      <p className="text-sm text-gray-700 break-all">
                        {item.PrimaryEmailAddr?.Address || "—"}
                      </p>
                      {item.PrimaryPhone?.FreeFormNumber && (
                        <p className="text-xs text-gray-600 mt-0.5">
                          {item.PrimaryPhone.FreeFormNumber}
                        </p>
                      )}
                    </div>

                    {/* Address */}
                    <div className="py-1.5 border-t border-gray-100">
                      <span className="text-xs text-gray-500 block mb-0.5">
                        Address
                      </span>
                      <p className="text-sm text-gray-500 break-words">
                        {formatAddress(item.BillAddr)}
                      </p>
                    </div>

                    {/* Balance */}
                    <div className="flex justify-between items-center py-1.5 border-t border-gray-100">
                      <span className="text-xs text-gray-500">Balance</span>
                      <p
                        className={`text-sm font-semibold ${
                          item.Balance > 0 ? "text-amber-600" : "text-[#008236]"
                        }`}
                      >
                        {formatCurrency(item.Balance, item.CurrencyRef?.value)}
                      </p>
                    </div>

                    {/* Status */}
                    <div className="flex justify-between items-center py-1.5 border-t border-gray-100">
                      <span className="text-xs text-gray-500">Status</span>
                      <StatusBadge active={item.Active} />
                    </div>

                    {/* Last Updated */}
                    <div className="flex justify-between items-start py-1.5 border-t border-gray-100">
                      <span className="text-xs text-gray-500 shrink-0">
                        Last Updated
                      </span>
                      <div className="text-right">
                        <p className="text-sm text-gray-700">
                          {formatDate(item.MetaData?.LastUpdatedTime)}
                        </p>
                        <p className="text-xs text-gray-600">
                          Created {formatDate(item.MetaData?.CreateTime)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ─── Add Customer Modal ─── */}
      <Transition appear show={isAddModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[999]" onClose={closeAddModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl bg-white rounded-2xl shadow-xl flex flex-col max-h-[90vh]">
                  {/* Modal Header */}
                  <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 shrink-0">
                    <div className="flex items-center gap-3">
                      <div className="min-w-9 w-9 h-9 rounded-xl bg-[#115B9D17] flex items-center justify-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="17"
                          height="17"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="text-[#115B9D]"
                        >
                          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                          <circle cx="9" cy="7" r="4" />
                          <line x1="19" y1="8" x2="19" y2="14" />
                          <line x1="22" y1="11" x2="16" y2="11" />
                        </svg>
                      </div>
                      <div>
                        <Dialog.Title className="text-base font-semibold text-gray-900">
                          Add New Customer
                        </Dialog.Title>
                        <p className="text-xs text-gray-600 mt-0.5">
                          Fill in the details to create a customer
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={closeAddModal}
                      className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-gray-600 transition-colors"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M18 6 6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Modal Body */}
                  <div className="px-6 py-5 overflow-y-auto flex-1 space-y-5 scrollbar">
                    {/* Basic Info */}
                    <div className="space-y-3">
                      <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest">
                        Basic Information
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <FormField
                          label="Full Name"
                          name="name"
                          value={addForm.name}
                          onChange={handleAddFormChange}
                          placeholder="e.g. John Doe"
                          error={addErrors.name}
                          required
                        />
                        <FormField
                          label="Email Address"
                          name="email"
                          type="email"
                          value={addForm.email}
                          onChange={handleAddFormChange}
                          placeholder="e.g. john@example.com"
                          error={addErrors.email}
                          required
                        />
                      </div>
                    </div>

                    {/* Billing Address */}
                    <div className="space-y-3">
                      <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest">
                        Billing Address
                      </p>
                      <div className="p-4 border border-gray-100 rounded-xl bg-gray-50/50 space-y-3">
                        <FormField
                          label="Street Line"
                          name="bill_line1"
                          value={addForm.bill_line1}
                          onChange={handleAddFormChange}
                          placeholder="e.g. 900 Main St."
                        />
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <FormField
                            label="City"
                            name="bill_city"
                            value={addForm.bill_city}
                            onChange={handleAddFormChange}
                            placeholder="e.g. Middlefield"
                          />
                          <FormField
                            label="Country / State"
                            name="bill_country"
                            value={addForm.bill_country}
                            onChange={handleAddFormChange}
                            placeholder="e.g. CA"
                          />
                          <FormField
                            label="Postal Code"
                            name="bill_postal_code"
                            value={addForm.bill_postal_code}
                            onChange={handleAddFormChange}
                            placeholder="e.g. 94303"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Shipping Address */}
                    <div className="space-y-3">
                      <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest">
                        Shipping Address
                      </p>
                      <div className="p-4 border border-gray-100 rounded-xl bg-gray-50/50 space-y-3">
                        <FormField
                          label="Street Line"
                          name="ship_line1"
                          value={addForm.ship_line1}
                          onChange={handleAddFormChange}
                          placeholder="e.g. 38921 S. Boise Ave"
                        />
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <FormField
                            label="City"
                            name="ship_city"
                            value={addForm.ship_city}
                            onChange={handleAddFormChange}
                            placeholder="e.g. Middlefield"
                          />
                          <FormField
                            label="Country / State"
                            name="ship_country"
                            value={addForm.ship_country}
                            onChange={handleAddFormChange}
                            placeholder="e.g. CA"
                          />
                          <FormField
                            label="Postal Code"
                            name="ship_postal_code"
                            value={addForm.ship_postal_code}
                            onChange={handleAddFormChange}
                            placeholder="e.g. 94304"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Modal Footer */}
                  <div className="flex flex-wrap items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 shrink-0">
                    <button
                      type="button"
                      onClick={closeAddModal}
                      disabled={addLoading}
                      className="inline-flex items-center gap-2 text-sm text-gray-600 hover:opacity-80 rounded-xl transition-colors border border-gray-200 bg-white font-medium px-5 py-2.5 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleAddCustomer}
                      disabled={addLoading}
                      className="w-fit inline-flex items-center gap-2 bg-[#115B9D] hover:opacity-80 text-white text-sm rounded-xl transition-colors font-medium px-5 py-2.5 disabled:opacity-60"
                    >
                      {addLoading ? (
                        <>
                          <svg
                            className="animate-spin"
                            xmlns="http://www.w3.org/2000/svg"
                            width="15"
                            height="15"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                          </svg>
                          Adding...
                        </>
                      ) : (
                        <>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="15"
                            height="15"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                            <circle cx="9" cy="7" r="4" />
                            <line x1="19" y1="8" x2="19" y2="14" />
                            <line x1="22" y1="11" x2="16" y2="11" />
                          </svg>
                          Add Customer
                        </>
                      )}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* ─── Customer Detail Modal ─── */}
      <Transition appear show={isViewModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[999]" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl bg-white rounded-2xl shadow-xl flex flex-col max-h-[90vh]">
                  {/* Modal Header */}
                  <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 shrink-0">
                    <div className="flex items-center gap-3">
                      <div className="min-w-9 w-9 h-9 rounded-xl bg-[#115B9D17] flex items-center justify-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="17"
                          height="17"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="text-[#115B9D]"
                        >
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                      </div>
                      <div>
                        <Dialog.Title className="text-base font-semibold text-gray-900">
                          Customer Details
                        </Dialog.Title>
                        {selectedCustomer && (
                          <div className="mt-1">
                            <StatusBadge active={selectedCustomer.Active} />
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={closeModal}
                      className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-gray-600 transition-colors"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M18 6 6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {selectedCustomer && (
                    <div className="px-6 py-5 overflow-y-auto flex-1 space-y-5 scrollbar">
                      {/* Avatar + Name */}
                      <div className="flex items-center gap-4 p-4 bg-[#115B9D08] border border-[#115B9D]/10 rounded-xl">
                        <div className="size-14 rounded-xl bg-[#115B9D17] border border-[#115B9D]/15 flex items-center justify-center shrink-0">
                          <span className="text-lg font-bold text-[#115B9D]">
                            {getInitials(selectedCustomer.DisplayName)}
                          </span>
                        </div>
                        <div>
                          <p className="text-base font-semibold text-gray-900">
                            {selectedCustomer.DisplayName || "—"}
                          </p>
                          {selectedCustomer.CompanyName && (
                            <p className="text-sm text-gray-500 mt-0.5">
                              {selectedCustomer.CompanyName}
                            </p>
                          )}
                          <p className="text-xs text-gray-600 mt-0.5">
                            {selectedCustomer.CurrencyRef?.name || "—"} ·
                            Domain: {selectedCustomer.domain}
                          </p>
                        </div>
                      </div>

                      {/* Contact Info */}
                      <div className="space-y-1">
                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest">
                          Contact Information
                        </p>
                        <div className="pt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {/* Email */}
                          <div className="flex items-start gap-3 p-3 border border-gray-100 rounded-xl bg-gray-50/50">
                            <div className="size-7 rounded-lg bg-[#115B9D17] flex items-center justify-center shrink-0 mt-0.5">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="13"
                                height="13"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="text-[#115B9D]"
                              >
                                <rect
                                  width="20"
                                  height="16"
                                  x="2"
                                  y="4"
                                  rx="2"
                                />
                                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                              </svg>
                            </div>
                            <div className="min-w-0">
                              <p className="text-[11px] text-gray-600 font-medium uppercase tracking-wide">
                                Email
                              </p>
                              <p className="text-sm text-gray-800 break-all mt-0.5">
                                {selectedCustomer.PrimaryEmailAddr?.Address ||
                                  "—"}
                              </p>
                            </div>
                          </div>

                          {/* Phone */}
                          <div className="flex items-start gap-3 p-3 border border-gray-100 rounded-xl bg-gray-50/50">
                            <div className="size-7 rounded-lg bg-[#115B9D17] flex items-center justify-center shrink-0 mt-0.5">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="13"
                                height="13"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="text-[#115B9D]"
                              >
                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.56 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                              </svg>
                            </div>
                            <div className="min-w-0">
                              <p className="text-[11px] text-gray-600 font-medium uppercase tracking-wide">
                                Phone
                              </p>
                              <p className="text-sm text-gray-800 mt-0.5 break-words">
                                {selectedCustomer.PrimaryPhone
                                  ?.FreeFormNumber || "—"}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Addresses */}
                      <div className="space-y-1">
                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest">
                          Addresses
                        </p>
                        <div className="pt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {[
                            {
                              label: "Billing Address",
                              addr: selectedCustomer.BillAddr,
                            },
                            {
                              label: "Shipping Address",
                              addr: selectedCustomer.ShipAddr,
                            },
                          ].map(({ label, addr }) => (
                            <div
                              key={label}
                              className="flex items-start gap-3 p-3 border border-gray-100 rounded-xl bg-gray-50/50"
                            >
                              <div className="size-7 rounded-lg bg-[#115B9D17] flex items-center justify-center shrink-0 mt-0.5">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="13"
                                  height="13"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="text-[#115B9D]"
                                >
                                  <path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1 1 16 0Z" />
                                  <circle cx="12" cy="10" r="3" />
                                </svg>
                              </div>
                              <div className="min-w-0">
                                <p className="text-[11px] text-gray-600 font-medium uppercase tracking-wide">
                                  {label}
                                </p>
                                <p className="text-sm text-gray-800 mt-0.5 break-words">
                                  {formatAddress(addr)}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Financial Summary */}
                      <div className="space-y-1">
                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest">
                          Financial Summary
                        </p>
                        <div className="border border-gray-100 rounded-xl overflow-hidden">
                          {[
                            {
                              label: "Balance",
                              value: formatCurrency(
                                selectedCustomer.Balance,
                                selectedCustomer.CurrencyRef?.value,
                              ),
                              highlight: selectedCustomer.Balance > 0,
                            },
                            {
                              label: "Balance with Jobs",
                              value: formatCurrency(
                                selectedCustomer.BalanceWithJobs,
                                selectedCustomer.CurrencyRef?.value,
                              ),
                              highlight: selectedCustomer.BalanceWithJobs > 0,
                            },
                            {
                              label: "Taxable",
                              value: selectedCustomer.Taxable ? "Yes" : "No",
                              highlight: false,
                            },
                            {
                              label: "Preferred Delivery",
                              value:
                                selectedCustomer.PreferredDeliveryMethod || "—",
                              highlight: false,
                            },
                          ].map((row) => (
                            <div
                              key={row.label}
                              className="flex gap-x-3 gap-y-2 items-center justify-between px-4 py-3 border-b border-gray-50 last:border-0"
                            >
                              <p className="text-sm text-gray-500 font-medium">
                                {row.label}
                              </p>
                              <p
                                className={`text-sm font-semibold ${row.highlight ? "text-amber-600" : "text-gray-800"}`}
                              >
                                {row.value}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Meta */}
                      <div className="space-y-1">
                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest">
                          Record Info
                        </p>
                        <div className="pt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {[
                            {
                              label: "Created On",
                              value: formatDate(
                                selectedCustomer.MetaData?.CreateTime,
                              ),
                            },
                            {
                              label: "Last Updated",
                              value: formatDate(
                                selectedCustomer.MetaData?.LastUpdatedTime,
                              ),
                            },
                            {
                              label: "Customer ID",
                              value: selectedCustomer.Id || "—",
                            },
                            {
                              label: "Print Name",
                              value: selectedCustomer.PrintOnCheckName || "—",
                            },
                          ].map((d) => (
                            <div
                              key={d.label}
                              className="p-3 border border-gray-100 rounded-xl bg-gray-50/50"
                            >
                              <p className="text-[11px] text-gray-600 font-medium uppercase tracking-wide">
                                {d.label}
                              </p>
                              <p className="text-sm text-gray-800 mt-0.5 break-words">
                                {d.value}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Modal Footer */}
                  <div className="flex flex-wrap items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 shrink-0">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="inline-flex items-center gap-2 text-sm text-gray-600 hover:opacity-80 rounded-xl transition-colors border border-gray-200 bg-white font-medium px-5 py-2.5"
                    >
                      Close
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </Layout>
  );
}
